import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddressEditDialog from './AddressEditDialog';
import { usePractice } from '../contexts/PracticeContext';
import AuthContext from '../contexts/AuthContext';

function AddressBlock({
  street1,
  street2,
  city,
  state,
  zip,
  emptyLabel = 'Not provided',
}) {
  const hasAny = street1 || street2 || city || state || zip;
  if (!hasAny) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <Box>
      {street1 && <Typography variant="body2">{street1}</Typography>}
      {street2 && <Typography variant="body2">{street2}</Typography>}
      {(city || state || zip) && (
        <Typography variant="body2">
          {[city, state].filter(Boolean).join(', ')} {zip}
        </Typography>
      )}
    </Box>
  );
}

export default function PracticeDetails() {
  const { selectedPractice, setSelectedPractice } = usePractice();
  const { tokens } = React.useContext(AuthContext);
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const isLab = Boolean(selectedPractice?.is_lab);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editAddress, setEditAddress] = React.useState(null);

  const getAddressByPurpose = (purpose) => {
    const list = selectedPractice?.addresses || [];
    return list.find(a => a?.purpose === purpose) || null;
  };

  if (!selectedPractice) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body1">
            No practice selected.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ backgroundColor: 'white', p: 2, borderRadius: 1 }}>
      <Grid container spacing={2}>
        {/* Overview */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Overview" />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">{selectedPractice.name || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">NPI</Typography>
                  <Typography variant="body1">{selectedPractice.npi || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">ID</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {selectedPractice.id ? String(selectedPractice.id).slice(0, 8) : '—'}
                    </Typography>
                    {selectedPractice.id && (
                      <Tooltip title="Copy full ID">
                        <IconButton
                          aria-label="copy id"
                          size="small"
                          onClick={() => navigator.clipboard?.writeText(String(selectedPractice.id))}
                        >
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
                {!isLab && (
                  <>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">Practice</Typography>
                      <Typography variant="body1">{selectedPractice.is_practice ? 'Yes' : 'No'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">On-site collections</Typography>
                      <Typography variant="body1">
                        {(() => {
                          const v = selectedPractice?.is_doing_in_house_collection;
                          return v === true ? 'Yes' : v === false ? 'No' : '—';
                        })()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">Collection Site</Typography>
                      <Typography variant="body1">{selectedPractice.is_collection_site ? 'Yes' : 'No'}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Address cards row */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {/* Corporate Address */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardHeader
                  title="Corporate Address"
                  action={(() => {
                    const a = getAddressByPurpose('corporate');
                    return a ? (
                      <IconButton aria-label="edit corporate" onClick={() => { setEditAddress(a); setEditOpen(true); }}>
                        <EditIcon />
                      </IconButton>
                    ) : null; // cannot add new, so no edit if missing
                  })()}
                />
                <Divider />
                <CardContent>
                  {(() => {
                    const a = getAddressByPurpose('corporate');
                    return (
                      <AddressBlock
                        street1={a?.street_1}
                        street2={a?.street_2}
                        city={a?.city}
                        state={a?.state}
                        zip={a?.zip_code}
                      />
                    );
                  })()}
                </CardContent>
              </Card>
            </Grid>

            {!isLab && (
              <Grid item xs={12} md={4}>
                <Card>
                  <CardHeader
                    title="Billing Address"
                    action={(() => {
                      const a = getAddressByPurpose('billing');
                      return a ? (
                        <IconButton aria-label="edit billing" onClick={() => { setEditAddress(a); setEditOpen(true); }}>
                          <EditIcon />
                        </IconButton>
                      ) : null;
                    })()}
                  />
                  <Divider />
                  <CardContent>
                    {(() => {
                      const a = getAddressByPurpose('billing');
                      return (
                        <AddressBlock
                          street1={a?.street_1}
                          street2={a?.street_2}
                          city={a?.city}
                          state={a?.state}
                          zip={a?.zip_code}
                        />
                      );
                    })()}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Shipping Address */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardHeader
                  title="Shipping Address"
                  action={(() => {
                    const a = getAddressByPurpose('shipping');
                    return a ? (
                      <IconButton aria-label="edit shipping" onClick={() => { setEditAddress(a); setEditOpen(true); }}>
                        <EditIcon />
                      </IconButton>
                    ) : null;
                  })()}
                />
                <Divider />
                <CardContent>
                  {(() => {
                    const a = getAddressByPurpose('shipping');
                    return (
                      <AddressBlock
                        street1={a?.street_1}
                        street2={a?.street_2}
                        city={a?.city}
                        state={a?.state}
                        zip={a?.zip_code}
                      />
                    );
                  })()}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {/* Address Edit Dialog */}
      {selectedPractice && editAddress && (
        <AddressEditDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => {
            // Update addresses in selected practice
            setSelectedPractice(prev => ({
              ...prev,
              addresses: (prev.addresses || []).map(a => a.id === updated.id ? updated : a),
            }));
            setEditOpen(false);
          }}
          orgId={selectedPractice.id}
          address={editAddress}
          addresses={selectedPractice.addresses || []}
          tokens={tokens}
          apiBase={apiBase}
        />
      )}
    </Box>
  );
}
